import { IntegrationKey } from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { checkAccessLandingMobilePage } from 'entities/ExportRouters/lib/checkAccessLandingMobilePage'
import { checkAccessSuspendedPage } from 'entities/ExportRouters/lib/checkAccessSuspendedPage'

export enum EnumAccessPageKeys {
  broadcasts = 'broadcasts',
  triggers = 'triggers',
  power_dialer = 'power_dialer',
  chatbot = 'chatbot',
  knowledge_base = 'knowledge_base',
  workflows = 'workflows',
  hubspot_workflows = 'hubspot_workflows',
  campaigns = 'campaigns',
  keywords = 'keywords',
  compliance_settings = 'compliance_settings',
  numbers_settings = 'numbers_settings',
  numbers_settings_subscription_canceled = 'numbers_settings_subscription_canceled',
  numbers_settings_add = 'numbers_settings_add',
  numbers_settings_buy_number = 'numbers_settings_buy_number',
  numbers_settings_add_from_app = 'numbers_settings_add_from_app',
  numbers_settings_add_landline = 'numbers_settings_add_landline',
  referrals = 'referrals',
  settings_email_to_sms = 'settings_email_to_sms',
  analytics = 'analytics',
  search_results_page = 'search_results_page',
  inboxes = 'inboxes',
  members = 'members',
  custom_fields = 'custom_fields',
  roles_permissions = 'roles_permissions',
  teams = 'teams',
  inbox_page = 'inbox_page',
  inbox_page_calling = 'inbox_page_calling',
  create_inbox = 'create_inbox',
  click_to_text = 'click_to_text',
  add_ons = 'add_ons',
  settings_organization = 'settings_organization',
  settings_profile = 'settings_profile',
  clients = 'clients',
  surveys = 'surveys',
  reviews = 'reviews',
  suspended = 'suspended',
  landingMobile = 'landingMobile',

  // developer
  authorized_apps = 'authorized_apps',
  access_tokens = 'access_tokens',
  applications = 'applications',

  // system
  system_health = 'system_health',
}

export const promiseCheckAccessPage = async (key: EnumAccessPageKeys, entities: IEntitiesStore) => {
  await Promise.all([
    entities.featureFlagsStore.fetchFeatureFlags(),
    entities.usersStore.initPromise,
    entities.userPermissionsStore.initPromise,
    entities.organizationStore.initPromise,
    entities.integrationsStore.initPromise,
    entities.subscriptionStore.initPromise,
    entities.userSettingsStore.initPromise,
    entities.organizationSettingsStore.initPromise,
  ])

  return checkAccessPage(key, entities)
}

export const checkAccessPage = (key: EnumAccessPageKeys, entities: IEntitiesStore) => {
  const {
    usersStore,
    featureFlagsStore,
    organizationStore,
    userPermissionsStore,
    integrationsStore,
    subscriptionStore,
    userSettingsStore,
    organizationSettingsStore,
  } = entities
  const user = usersStore.user
  const isViewOnlyRole = user?.isViewOnlyRole
  const permissionCanManageBroadcasts = userPermissionsStore.getItem('can_manage_broadcasts')
  const permissionCanManageTriggers = userPermissionsStore.getItem('can_manage_triggers')
  const permissionCanManageTextBots = userPermissionsStore.getItem('can_manage_text_bots')
  const permissionCanManageReviews = userPermissionsStore.getItem('can_manage_reviews')
  const permissionCanManageSurveys = userPermissionsStore.getItem('can_access_surveys')
  const permissionCanManageCampaigns = userPermissionsStore.getItem('can_manage_campaigns')
  const permissionCanAccessAnalytics = userPermissionsStore.getItem('can_access_analytics')
  const permissionCanAccessInboxPage = userPermissionsStore.getItem('can_access_inboxes')
  const permissionCanCreateInbox = userPermissionsStore.getItem('can_create_additional_inboxes')
  const permissionCanManageOrganizationInboxes = userPermissionsStore.getItem(
    'can_manage_organization_inboxes'
  )
  const permissionCanManageMembers = userPermissionsStore.getItem('can_manage_members')
  const permissionCanManageClients = userPermissionsStore.getItem('can_manage_clients')
  const permissionCanManageCustomFields = userPermissionsStore.getItem('can_manage_custom_fields')
  const hasPublicApiClient =
    userSettingsStore.hasPublicApiClient || organizationSettingsStore.hasPublicApiClient

  const canManageBroadcasts = !Boolean(!permissionCanManageBroadcasts || isViewOnlyRole)
  const canManageTriggers = !Boolean(!permissionCanManageTriggers || isViewOnlyRole)
  const canManagePowerDialer = !Boolean(isViewOnlyRole)
  const canManageTextBots = !Boolean(isViewOnlyRole || !permissionCanManageTextBots)
  const canManageKnowledgeBase = !Boolean(isViewOnlyRole || !permissionCanManageTextBots)
  const canManageWorkflows = !Boolean(isViewOnlyRole)
  const canManageCampaigns = !Boolean(isViewOnlyRole || !permissionCanManageCampaigns)
  const canManageKeywords = !Boolean(user?.isViewOnlyRole)
  const canManageClickToText = !Boolean(user?.isViewOnlyRole || !featureFlagsStore.click_to_text)
  const canManageComplianceSettings = !organizationStore.isTwilioNumberVendor

  // numbers
  const canManageNumbersSettings = !Boolean(user?.isViewOnlyRole)
  const canManageNumbersSettingsSubscriptionCanceled = !Boolean(subscriptionStore.isCanceled)
  const canManageNumbersSettingsAdd = Boolean(user?.isAdminOrOwnerRole)
  const canManageNumbersSettingsBuyNumber = Boolean(user?.isAdminOrOwnerRole)
  const canManageNumbersSettingsAddFromApp = Boolean(user?.isAdminOrOwnerRole)
  const canManageNumbersSettingsAddLandline = Boolean(user?.isAdminOrOwnerRole)
  const canManageHubspotWorkflows = Boolean(
    featureFlagsStore.hubspot_workflow_statistics &&
      integrationsStore.getIntegration(IntegrationKey.hubspot)?.isConnected
  )
  const canManageReferrals = !Boolean(isViewOnlyRole)
  const canManageSettingsEmailToSms = Boolean(
    (featureFlagsStore.salesmsg_design_v2_general_org_setting ||
      featureFlagsStore.salesmsg_design_v2_email_to_sms_org_setting) &&
      !organizationStore.isHipaaCompliant
  )
  const canManageAnalytics = Boolean(permissionCanAccessAnalytics && !isViewOnlyRole)

  const canManageInboxes = permissionCanAccessInboxPage
  const canManageMembers = permissionCanManageMembers
  const canManageCustomFields = permissionCanManageCustomFields
  const canManageRolesPermissions = Boolean(canManageMembers && user?.isAdminOrOwnerRole)
  const canManageTeams = Boolean(
    user?.isAdminOrOwnerRole && featureFlagsStore.teams_flow && !organizationStore.isClient
  )
  const canManageInboxPage = permissionCanManageOrganizationInboxes
  const canManageInboxPageCalling = featureFlagsStore.settings_inboxes_manage_inbox_calling_v2
  const canCreateInbox = Boolean(permissionCanCreateInbox)

  const canManageConversationSearch = featureFlagsStore.search_results_page

  const canManageSettingsProfile = true
  const canManageSettingsOrganization = Boolean(user && !user.isViewOnlyRole && !user.isMemberRole)
  const canManageClients = Boolean(
    featureFlagsStore.salesmsg_design_v2_clients &&
      ((permissionCanManageClients && organizationStore.isAgency) ||
        user?.hasAccessibleOrganizations)
  )
  const canManageSurveys = Boolean(
    featureFlagsStore.surveys && !isViewOnlyRole && permissionCanManageSurveys
  )
  const canManageReviews = Boolean(
    featureFlagsStore.reviews && !isViewOnlyRole && permissionCanManageReviews
  )

  // developer
  const canManageAuthorizedApps = !subscriptionStore.isCanceled
  const canManageAccessTokens = !subscriptionStore.isCanceled && hasPublicApiClient
  const canManageApplications = !subscriptionStore.isCanceled && hasPublicApiClient

  switch (key) {
    case EnumAccessPageKeys.broadcasts:
      return canManageBroadcasts
    case EnumAccessPageKeys.triggers:
      return canManageTriggers
    case EnumAccessPageKeys.power_dialer:
      return canManagePowerDialer
    case EnumAccessPageKeys.chatbot:
      return canManageTextBots
    case EnumAccessPageKeys.knowledge_base:
      return canManageKnowledgeBase
    case EnumAccessPageKeys.workflows:
      return canManageWorkflows
    case EnumAccessPageKeys.campaigns:
      return canManageCampaigns
    case EnumAccessPageKeys.keywords:
      return canManageKeywords
    case EnumAccessPageKeys.click_to_text:
      return canManageClickToText
    case EnumAccessPageKeys.compliance_settings:
      return canManageComplianceSettings

    // numbers
    case EnumAccessPageKeys.numbers_settings:
      return canManageNumbersSettings
    case EnumAccessPageKeys.numbers_settings_add:
      return canManageNumbersSettingsAdd
    case EnumAccessPageKeys.numbers_settings_subscription_canceled:
      return canManageNumbersSettingsSubscriptionCanceled
    case EnumAccessPageKeys.numbers_settings_buy_number:
      return canManageNumbersSettingsBuyNumber
    case EnumAccessPageKeys.numbers_settings_add_from_app:
      return canManageNumbersSettingsAddFromApp
    case EnumAccessPageKeys.numbers_settings_add_landline:
      return canManageNumbersSettingsAddLandline

    case EnumAccessPageKeys.hubspot_workflows:
      return canManageHubspotWorkflows
    case EnumAccessPageKeys.referrals:
      return canManageReferrals
    case EnumAccessPageKeys.settings_email_to_sms:
      return canManageSettingsEmailToSms
    case EnumAccessPageKeys.analytics:
      return canManageAnalytics
    case EnumAccessPageKeys.search_results_page:
      return canManageConversationSearch
    case EnumAccessPageKeys.inboxes:
      return canManageInboxes
    case EnumAccessPageKeys.members:
      return canManageMembers
    case EnumAccessPageKeys.custom_fields:
      return canManageCustomFields
    case EnumAccessPageKeys.roles_permissions:
      return canManageRolesPermissions
    case EnumAccessPageKeys.teams:
      return canManageTeams
    case EnumAccessPageKeys.inbox_page:
      return canManageInboxPage
    case EnumAccessPageKeys.inbox_page_calling:
      return canManageInboxPageCalling
    case EnumAccessPageKeys.create_inbox:
      return canCreateInbox
    case EnumAccessPageKeys.add_ons:
      return Boolean(user?.isAdminOrOwnerRole)

    case EnumAccessPageKeys.settings_organization:
      return canManageSettingsOrganization

    case EnumAccessPageKeys.settings_profile:
      return canManageSettingsProfile
    case EnumAccessPageKeys.clients:
      return canManageClients
    case EnumAccessPageKeys.surveys:
      return canManageSurveys
    case EnumAccessPageKeys.reviews:
      return canManageReviews
    case EnumAccessPageKeys.suspended:
      return checkAccessSuspendedPage(entities)
    case EnumAccessPageKeys.landingMobile:
      return checkAccessLandingMobilePage()

    // developer
    case EnumAccessPageKeys.authorized_apps:
      return canManageAuthorizedApps
    case EnumAccessPageKeys.access_tokens:
      return canManageAccessTokens
    case EnumAccessPageKeys.applications:
      return canManageApplications
    case EnumAccessPageKeys.system_health:
      return featureFlagsStore.system_health_dashboard
    default:
      return true
  }
}
